import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { CalendarModule } from 'primeng/calendar';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';

import { AnalyticsService } from '../../../service/admin/analytics.service';
import { UserService } from '../../../service/admin/user.service';
import { ToasterService } from '../../../service/common/toaster.service';
import { WorkContextBarComponent } from '../../shared/work-context-bar/work-context-bar.component';
import { extractHttpErrorMessage } from '../../../utils/http-error.util';

type RangePreset = 'today' | '7d' | '30d' | 'month' | 'last_month' | 'custom';
type SortKey = 'date' | 'total' | 'pending' | 'patients';
type ReportView = 'day' | 'patient';
type PatientSortKey = 'name' | 'collected' | 'outstanding' | 'visits';

interface PatientPaymentLine {
  kind: 'visit' | 'dues';
  date: string;
  mode: string;
  fees: number;
  cash: number;
  online: number;
  dues_settled: number;
  collected: number;
  pending: number;
  discount_percent: number;
}

interface PatientPaymentRow {
  patient_id: number;
  patient_name: string;
  patient_number: string;
  contact_number: string;
  visits: number;
  fees: number;
  cash: number;
  online: number;
  dues_settled: number;
  collected: number;
  visit_pending: number;
  outstanding: number;
  lines: PatientPaymentLine[];
}

interface DailyRow {
  collection_date: string;
  patients: number;
  fees: number;
  cash: number;
  online: number;
  total: number;
  pending: number;
}

interface PatientRow {
  patient_name: string;
  visit_fees: number;
  received_cash: number;
  received_online: number;
  total_received: number;
  pending: number;
}

@Component({
  standalone: true,
  selector: 'app-analytics-collections',
  imports: [
    CommonModule,
    FormsModule,
    CalendarModule,
    DialogModule,
    TooltipModule,
    WorkContextBarComponent,
  ],
  providers: [AnalyticsService, UserService],
  templateUrl: './analytics-collections.component.html',
  styleUrl: './analytics-collections.component.scss'
})
export class AnalyticsCollectionsComponent implements OnInit, OnDestroy {
  filters = {
    start_date: null as Date | null,
    end_date: null as Date | null,
    doctor_id: 1 as number | null,
    include_free: true
  };

  /** PrimeNG range calendar model: [start, end] */
  dateRange: Date[] | null = null;

  report = {
    summary: {
      total_appointments: 0,
      fees_generated: 0,
      cash_collected: 0,
      online_collected: 0,
      total_collected: 0,
      total_pending: 0
    },
    daily_breakdown: [] as DailyRow[]
  };

  reportView: ReportView = 'day';
  patientReport = {
    summary: {
      patients: 0,
      visits: 0,
      fees: 0,
      cash: 0,
      online: 0,
      dues_settled: 0,
      collected: 0,
      visit_pending: 0,
      outstanding: 0,
    },
    patients: [] as PatientPaymentRow[],
  };
  patientReportQuery = '';
  patientSortKey: PatientSortKey = 'name';
  patientSortDir: 'asc' | 'desc' = 'asc';
  expandedPatientId: number | null = null;
  patientPage = 0;
  readonly patientPageSize = 20;

  visibleModal = false;
  dailyDetails: PatientRow[] = [];
  selectedDate = '';
  selectedDay: DailyRow | null = null;
  doctorsList: { label: string; value: number }[] = [];

  loading = false;
  detailsLoading = false;
  exporting = false;
  exportingDaily = false;
  hasGenerated = false;

  activePreset: RangePreset = '7d';
  sortKey: SortKey = 'date';
  sortDir: 'asc' | 'desc' = 'desc';
  tableQuery = '';
  patientQuery = '';

  pinRequired = false;
  pinConfigured = false;
  pinUnlocked = true;
  pinReady = true;
  pinBusy = false;
  pinError = '';
  pinDigits = '';
  pinPassword = '';
  pinCheckFailed = false;
  pinStep: 'unlock' | 'create' | 'confirm' | 'reset-password' | 'reset-create' | 'reset-confirm' | 'change-current' | 'change-create' | 'change-confirm' = 'unlock';
  private pinDraft = '';
  private pinCurrent = '';
  readonly pinPad = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

  readonly presets: { id: RangePreset; label: string }[] = [
    { id: 'today', label: 'Today' },
    { id: '7d', label: '7 days' },
    { id: '30d', label: '30 days' },
    { id: 'month', label: 'This month' },
    { id: 'last_month', label: 'Last month' },
  ];

  constructor(
    private analyticsService: AnalyticsService,
    private toaster: ToasterService,
    private userService: UserService,
  ) {}

  ngOnInit(): void {
    this.applyPreset('7d', false);
    if (this.requiresCollectionsPin()) {
      this.pinRequired = true;
      this.pinUnlocked = false;
      this.pinReady = false;
    }
    this.getDoctors();
    this.loadCollectionsPinStatus();
  }

  ngOnDestroy(): void {
    this.analyticsService.lockCollectionsPin().subscribe({
      error: () => undefined,
    });
  }

  get collectionRate(): number {
    const fees = Number(this.report.summary.fees_generated) || 0;
    const collected = Number(this.report.summary.total_collected) || 0;
    if (fees <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((collected / fees) * 100));
  }

  get rateCircumference(): number {
    return 2 * Math.PI * 54;
  }

  get rateOffset(): number {
    return this.rateCircumference * (1 - this.collectionRate / 100);
  }

  get cashShare(): number {
    const total = Number(this.report.summary.total_collected) || 0;
    if (total <= 0) {
      return 0;
    }
    return Math.round((Number(this.report.summary.cash_collected) / total) * 100);
  }

  get onlineShare(): number {
    const total = Number(this.report.summary.total_collected) || 0;
    if (total <= 0) {
      return 0;
    }
    return Math.max(0, 100 - this.cashShare);
  }

  get dayCount(): number {
    return this.report.daily_breakdown?.length ?? 0;
  }

  get avgDailyCollected(): number {
    if (!this.dayCount) {
      return 0;
    }
    return Math.round(Number(this.report.summary.total_collected) / this.dayCount);
  }

  get avgPerPatient(): number {
    const n = Number(this.report.summary.total_appointments) || 0;
    if (!n) {
      return 0;
    }
    return Math.round(Number(this.report.summary.total_collected) / n);
  }

  get bestDay(): DailyRow | null {
    if (!this.report.daily_breakdown.length) {
      return null;
    }
    return this.report.daily_breakdown.reduce((best, day) =>
      Number(day.total) > Number(best.total) ? day : best
    );
  }

  get maxDailyChartValue(): number {
    if (!this.report.daily_breakdown.length) {
      return 1;
    }
    return Math.max(
      ...this.report.daily_breakdown.map((d) =>
        Math.max(Number(d.fees) || 0, Number(d.total) || 0)
      ),
      1
    );
  }

  get maxDailyTotal(): number {
    if (!this.report.daily_breakdown.length) {
      return 1;
    }
    return Math.max(...this.report.daily_breakdown.map((d) => Number(d.total) || 0), 1);
  }

  /** Rounded Y-axis ceiling so tick marks stay readable. */
  get chartScaleMax(): number {
    return this.niceCeil(this.maxDailyChartValue);
  }

  get yAxisTicks(): { value: number; label: string; pct: number }[] {
    const max = this.chartScaleMax || 1;
    const steps = 4;
    const ticks: { value: number; label: string; pct: number }[] = [];
    for (let i = steps; i >= 0; i--) {
      const value = (max / steps) * i;
      ticks.push({
        value,
        label: this.formatAxisValue(value),
        pct: (value / max) * 100,
      });
    }
    return ticks;
  }

  get sortedDays(): DailyRow[] {
    const q = this.tableQuery.trim().toLowerCase();
    let rows = [...this.report.daily_breakdown];

    if (q) {
      rows = rows.filter((d) =>
        this.formatDisplayDate(d.collection_date).toLowerCase().includes(q) ||
        String(d.collection_date).includes(q)
      );
    }

    const dir = this.sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      let av: number | string = 0;
      let bv: number | string = 0;
      switch (this.sortKey) {
        case 'total':
          av = Number(a.total) || 0;
          bv = Number(b.total) || 0;
          break;
        case 'pending':
          av = Number(a.pending) || 0;
          bv = Number(b.pending) || 0;
          break;
        case 'patients':
          av = Number(a.patients) || 0;
          bv = Number(b.patients) || 0;
          break;
        default:
          av = a.collection_date;
          bv = b.collection_date;
      }
      if (av < bv) {
        return -1 * dir;
      }
      if (av > bv) {
        return 1 * dir;
      }
      return 0;
    });

    return rows;
  }

  get chartDays(): DailyRow[] {
    return [...this.report.daily_breakdown].sort((a, b) =>
      a.collection_date.localeCompare(b.collection_date)
    );
  }

  get filteredPatients(): PatientRow[] {
    const q = this.patientQuery.trim().toLowerCase();
    if (!q) {
      return this.dailyDetails;
    }
    return this.dailyDetails.filter((p) =>
      (p.patient_name || '').toLowerCase().includes(q)
    );
  }

  get dayDetailsTotals() {
    return this.dailyDetails.reduce(
      (acc, p) => {
        acc.fees += Number(p.visit_fees) || 0;
        acc.cash += Number(p.received_cash) || 0;
        acc.online += Number(p.received_online) || 0;
        acc.received += Number(p.total_received) || 0;
        acc.pending += Number(p.pending) || 0;
        return acc;
      },
      { fees: 0, cash: 0, online: 0, received: 0, pending: 0 }
    );
  }

  get rangeLabel(): string {
    if (!this.filters.start_date || !this.filters.end_date) {
      return '';
    }
    const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
    const a = this.filters.start_date.toLocaleDateString('en-IN', opts);
    const b = this.filters.end_date.toLocaleDateString('en-IN', {
      ...opts,
      year: 'numeric',
    });
    return `${a} – ${b}`;
  }

  get doctorLabel(): string {
    if (!this.filters.doctor_id) {
      return 'All doctors';
    }
    return this.doctorsList.find((d) => d.value === this.filters.doctor_id)?.label ?? `Doctor #${this.filters.doctor_id}`;
  }

  setReportView(view: ReportView): void {
    if (this.reportView === view) {
      return;
    }
    this.reportView = view;
    this.loadReport();
  }

  get filteredPatientPayments(): PatientPaymentRow[] {
    const q = this.patientReportQuery.trim().toLowerCase();
    let rows = [...this.patientReport.patients];
    if (q) {
      rows = rows.filter((row) =>
        row.patient_name.toLowerCase().includes(q) ||
        row.patient_number.toLowerCase().includes(q) ||
        row.contact_number.toLowerCase().includes(q)
      );
    }

    const dir = this.patientSortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      let av: number | string = 0;
      let bv: number | string = 0;
      switch (this.patientSortKey) {
        case 'collected':
          av = a.collected;
          bv = b.collected;
          break;
        case 'outstanding':
          av = a.outstanding;
          bv = b.outstanding;
          break;
        case 'visits':
          av = a.visits;
          bv = b.visits;
          break;
        default:
          av = a.patient_name.toLowerCase();
          bv = b.patient_name.toLowerCase();
      }
      if (av < bv) {
        return -1 * dir;
      }
      if (av > bv) {
        return 1 * dir;
      }
      return 0;
    });
    return rows;
  }

  get patientPageCount(): number {
    return Math.max(1, Math.ceil(this.filteredPatientPayments.length / this.patientPageSize));
  }

  get pagedPatientPayments(): PatientPaymentRow[] {
    const page = Math.min(this.patientPage, this.patientPageCount - 1);
    const start = Math.max(0, page) * this.patientPageSize;
    return this.filteredPatientPayments.slice(start, start + this.patientPageSize);
  }

  get patientPageLabel(): string {
    const total = this.filteredPatientPayments.length;
    if (!total) {
      return '';
    }
    const page = Math.min(this.patientPage, this.patientPageCount - 1);
    const start = page * this.patientPageSize + 1;
    const end = Math.min(total, start + this.patientPageSize - 1);
    return `${start}–${end} of ${total}`;
  }

  onPatientSearch(): void {
    this.patientPage = 0;
    this.expandedPatientId = null;
  }

  prevPatientPage(): void {
    if (this.patientPage <= 0) {
      return;
    }
    this.patientPage -= 1;
    this.expandedPatientId = null;
  }

  nextPatientPage(): void {
    if (this.patientPage >= this.patientPageCount - 1) {
      return;
    }
    this.patientPage += 1;
    this.expandedPatientId = null;
  }

  setPatientSort(key: PatientSortKey): void {
    if (this.patientSortKey === key) {
      this.patientSortDir = this.patientSortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.patientSortKey = key;
      this.patientSortDir = key === 'name' ? 'asc' : 'desc';
    }
    this.patientPage = 0;
    this.expandedPatientId = null;
  }

  patientSortIcon(key: PatientSortKey): string {
    if (this.patientSortKey !== key) {
      return 'pi-sort-alt';
    }
    return this.patientSortDir === 'asc' ? 'pi-sort-amount-up-alt' : 'pi-sort-amount-down-alt';
  }

  togglePatientPayments(patientId: number): void {
    this.expandedPatientId = this.expandedPatientId === patientId ? null : patientId;
  }

  selectDoctor(doctorId: number | null): void {
    if (this.filters.doctor_id === doctorId) {
      return;
    }
    this.filters.doctor_id = doctorId;
    this.loadReport();
  }

  get collectedPolylinePoints(): string {
    const days = this.chartDays;
    const n = days.length;
    if (!n) {
      return '';
    }
    const max = this.chartScaleMax || 1;
    return days
      .map((day, i) => {
        const x = ((i + 0.5) / n) * 100;
        const y = 100 - Math.min(100, ((Number(day.total) || 0) / max) * 100);
        return `${x},${y}`;
      })
      .join(' ');
  }

  barHeight(day: DailyRow): number {
    return this.seriesHeight(day.total);
  }

  feesHeight(day: DailyRow): number {
    return this.seriesHeight(day.fees);
  }

  private seriesHeight(value: unknown): number {
    const max = this.chartScaleMax || 1;
    const amount = Number(value) || 0;
    if (amount <= 0) {
      return 0;
    }
    return Math.max(3, Math.round((amount / max) * 100));
  }

  dayChartTooltip(day: DailyRow): string {
    return (
      `${this.formatDisplayDate(day.collection_date)}\n` +
      `Generated ₹${this.formatRupee(day.fees)}\n` +
      `Collected ₹${this.formatRupee(day.total)}`
    );
  }

  formatAxisValue(value: number): string {
    const amount = Number(value) || 0;
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(amount % 100000 === 0 ? 0 : 1)}L`;
    }
    if (amount >= 1000) {
      return `₹${(amount / 1000).toFixed(amount % 1000 === 0 ? 0 : 1)}k`;
    }
    return `₹${Math.round(amount).toLocaleString('en-IN')}`;
  }

  private niceCeil(value: number): number {
    if (!Number.isFinite(value) || value <= 0) {
      return 1000;
    }
    const exp = Math.pow(10, Math.floor(Math.log10(value)));
    const fraction = value / exp;
    let nice: number;
    if (fraction <= 1) {
      nice = 1;
    } else if (fraction <= 2) {
      nice = 2;
    } else if (fraction <= 5) {
      nice = 5;
    } else {
      nice = 10;
    }
    return nice * exp;
  }

  cashPct(day: DailyRow): number {
    const total = Number(day.total) || 0;
    if (total <= 0) {
      return 0;
    }
    return Math.round((Number(day.cash) / total) * 100);
  }

  onlinePct(day: DailyRow): number {
    const total = Number(day.total) || 0;
    if (total <= 0) {
      return 0;
    }
    return Math.max(0, 100 - this.cashPct(day));
  }

  dayRate(day: DailyRow): number {
    const fees = Number(day.fees) || 0;
    if (fees <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((Number(day.total) / fees) * 100));
  }

  setSort(key: SortKey): void {
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      return;
    }
    this.sortKey = key;
    this.sortDir = key === 'date' ? 'desc' : 'desc';
  }

  sortIcon(key: SortKey): string {
    if (this.sortKey !== key) {
      return 'pi-sort-alt';
    }
    return this.sortDir === 'asc' ? 'pi-sort-amount-up-alt' : 'pi-sort-amount-down-alt';
  }

  applyPreset(id: RangePreset, reload = true): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let start = new Date(today);
    let end = new Date(today);

    switch (id) {
      case 'today':
        break;
      case '7d':
        start.setDate(today.getDate() - 6);
        break;
      case '30d':
        start.setDate(today.getDate() - 29);
        break;
      case 'month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'last_month':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      default:
        this.activePreset = 'custom';
        return;
    }

    this.activePreset = id;
    this.filters.start_date = start;
    this.filters.end_date = end;
    this.dateRange = [start, end];
    if (reload) {
      this.loadReport();
    }
  }

  onDateRangeSelect(): void {
    const range = this.dateRange;
    if (!range?.length) {
      this.filters.start_date = null;
      this.filters.end_date = null;
      this.activePreset = 'custom';
      return;
    }

    this.filters.start_date = range[0] ?? null;
    this.filters.end_date = range[1] ?? range[0] ?? null;
    this.activePreset = 'custom';
  }

  formatDate(date: Date | null): string {
    if (!date) {
      return '';
    }
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  formatRupee(value: unknown): string {
    const amount = Number(value ?? 0);
    if (!Number.isFinite(amount)) {
      return '0';
    }
    return amount.toLocaleString('en-IN');
  }

  visitPendingLabel(value: unknown): string {
    const amount = Number(value) || 0;
    if (amount < 0) {
      return `₹${this.formatRupee(Math.abs(amount))} extra`;
    }
    return `₹${this.formatRupee(amount)}`;
  }

  lineTone(line: PatientPaymentLine): string {
    if (line.kind === 'dues') {
      return 'is-dues';
    }
    if (line.pending > 0) {
      return 'is-short';
    }
    if (line.pending < 0) {
      return 'is-extra';
    }
    return 'is-settled';
  }

  lineNote(line: PatientPaymentLine): string {
    if (line.kind === 'dues') {
      const mode = line.mode || 'Payment';
      return `${mode} of ₹${this.formatRupee(line.collected)} settled older dues.`;
    }
    if (line.pending > 0) {
      return `₹${this.formatRupee(line.pending)} of this visit fee is still unpaid.`;
    }
    if (line.pending < 0) {
      return `₹${this.formatRupee(Math.abs(line.pending))} above the visit fee went toward earlier dues.`;
    }
    return 'The visit fee was collected in full.';
  }

  lineResult(line: PatientPaymentLine): string {
    if (line.kind === 'dues') {
      return line.mode ? `${line.mode} dues` : 'Older dues';
    }
    if (line.pending > 0) {
      return `Unpaid ₹${this.formatRupee(line.pending)}`;
    }
    if (line.pending < 0) {
      return `Extra ₹${this.formatRupee(Math.abs(line.pending))}`;
    }
    return 'Paid';
  }

  formatLedgerDate(value: string): string {
    if (!value) {
      return '—';
    }
    const parsed = new Date(value.includes('T') ? value : `${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  formatCompact(value: unknown): string {
    const amount = Number(value ?? 0);
    if (!Number.isFinite(amount)) {
      return '₹0';
    }
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(amount >= 1000000 ? 1 : 2)}L`;
    }
    if (amount >= 1000) {
      return `₹${(amount / 1000).toFixed(amount >= 10000 ? 0 : 1)}k`;
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  }

  formatDisplayDate(value: string): string {
    if (!value) {
      return '—';
    }
    const parsed = new Date(value.includes('T') ? value : `${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      weekday: 'short',
    });
  }

  formatShortDate(value: string): string {
    if (!value) {
      return '';
    }
    const parsed = new Date(value.includes('T') ? value : `${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  }

  getPatientInitial(name: string | null | undefined): string {
    const trimmed = (name || '').trim();
    if (!trimmed) {
      return '?';
    }
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  isBestDay(day: DailyRow): boolean {
    return !!this.bestDay && this.bestDay.collection_date === day.collection_date;
  }

  getDoctors(): void {
    this.userService.getDoctors().subscribe({
      next: (res: any) => {
        this.doctorsList = Array.isArray(res.data)
          ? res.data.map((doctor: any) => ({
              label: doctor.name,
              value: doctor.id
            }))
          : [];
      },
      error: () => {
        this.doctorsList = [];
      }
    });
  }

  clearFilters(): void {
    this.filters.doctor_id = 1;
    this.tableQuery = '';
    this.patientReportQuery = '';
    this.applyPreset('7d');
  }

  loadReport(): void {
    if (this.collectionsPinGateVisible) {
      return;
    }
    if (!this.filters.start_date || !this.filters.end_date) {
      this.toaster.warn('Select start and end date', 'Invalid');
      return;
    }
    if (this.reportView === 'patient') {
      this.loadPatientReport();
      return;
    }

    const params = {
      start_date: this.formatDate(this.filters.start_date),
      end_date: this.formatDate(this.filters.end_date),
      doctor_id: this.filters.doctor_id ?? '',
      include_free: this.filters.include_free
    };

    this.loading = true;
    this.analyticsService.getCollections(params).subscribe({
      next: (res: any) => {
        this.report.summary = {
          total_appointments: Number(res.summary?.total_appointments) || 0,
          fees_generated: Number(res.summary?.fees_generated) || 0,
          cash_collected: Number(res.summary?.cash_collected) || 0,
          online_collected: Number(res.summary?.online_collected) || 0,
          total_collected: Number(res.summary?.total_collected) || 0,
          total_pending: Number(res.summary?.total_pending) || 0,
        };
        this.report.daily_breakdown = (res.daily_breakdown ?? []).map((d: any) => ({
          collection_date: d.collection_date,
          patients: Number(d.patients) || 0,
          fees: Number(d.fees) || 0,
          cash: Number(d.cash) || 0,
          online: Number(d.online) || 0,
          total: Number(d.total) || 0,
          pending: Number(d.pending) || 0,
        }));
        this.hasGenerated = true;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        if (this.relockCollections(err)) {
          return;
        }
        this.toaster.error('Could not load collections report', 'Failed');
      }
    });
  }

  loadPatientReport(): void {
    if (!this.filters.start_date || !this.filters.end_date) {
      return;
    }

    const params = {
      start_date: this.formatDate(this.filters.start_date),
      end_date: this.formatDate(this.filters.end_date),
      doctor_id: this.filters.doctor_id ?? '',
    };

    this.loading = true;
    this.analyticsService.getPatientPayments(params).subscribe({
      next: (res: any) => {
        const summary = res?.summary || {};
        this.patientReport = {
          summary: {
            patients: Number(summary.patients) || 0,
            visits: Number(summary.visits) || 0,
            fees: Number(summary.fees) || 0,
            cash: Number(summary.cash) || 0,
            online: Number(summary.online) || 0,
            dues_settled: Number(summary.dues_settled) || 0,
            collected: Number(summary.collected) || 0,
            visit_pending: Number(summary.visit_pending) || 0,
            outstanding: Number(summary.outstanding) || 0,
          },
          patients: (res?.patients || []).map((patient: any) => this.mapPatientPayment(patient)),
        };
        this.expandedPatientId = null;
        this.patientPage = 0;
        this.hasGenerated = true;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        if (this.relockCollections(err)) {
          return;
        }
        this.toaster.error('Could not load patient payments', 'Failed');
      },
    });
  }

  private mapPatientPayment(patient: any): PatientPaymentRow {
    return {
      patient_id: Number(patient.patient_id) || 0,
      patient_name: patient.patient_name || '',
      patient_number: patient.patient_number || '',
      contact_number: patient.contact_number || '',
      visits: Number(patient.visits) || 0,
      fees: Number(patient.fees) || 0,
      cash: Number(patient.cash) || 0,
      online: Number(patient.online) || 0,
      dues_settled: Number(patient.dues_settled) || 0,
      collected: Number(patient.collected) || 0,
      visit_pending: Number(patient.visit_pending) || 0,
      outstanding: Number(patient.outstanding) || 0,
      lines: Array.isArray(patient.lines)
        ? patient.lines.map((line: any) => ({
            kind: line.kind === 'dues' ? 'dues' : 'visit',
            date: line.date || '',
            mode: line.mode || '',
            fees: Number(line.fees) || 0,
            cash: Number(line.cash) || 0,
            online: Number(line.online) || 0,
            dues_settled: Number(line.dues_settled) || 0,
            collected: Number(line.collected) || 0,
            pending: Number(line.pending) || 0,
            discount_percent: Number(line.discount_percent) || 0,
          }))
        : [],
    };
  }

  openDailyDetails(day: DailyRow): void {
    if (this.collectionsPinGateVisible) {
      return;
    }
    this.selectedDate = day.collection_date;
    this.selectedDay = day;
    this.patientQuery = '';
    this.detailsLoading = true;
    this.visibleModal = true;
    this.dailyDetails = [];

    this.analyticsService.getDailyPatientDetails({
      date: day.collection_date,
      doctor_id: this.filters.doctor_id ?? '',
    }).subscribe({
      next: (res: any) => {
        this.dailyDetails = (res.patients ?? []).map((p: any) => ({
          patient_name: p.patient_name,
          visit_fees: Number(p.visit_fees) || 0,
          received_cash: Number(p.received_cash) || 0,
          received_online: Number(p.received_online) || 0,
          total_received: Number(p.total_received) || 0,
          pending: Number(p.pending) || 0,
        }));
        this.detailsLoading = false;
      },
      error: (err) => {
        this.dailyDetails = [];
        this.detailsLoading = false;
        this.visibleModal = false;
        if (this.relockCollections(err)) {
          return;
        }
        this.toaster.error('Could not load day details', 'Failed');
      }
    });
  }

  exportActiveReport(): void {
    if (this.reportView === 'patient') {
      this.exportPatientPayments();
      return;
    }
    this.exportCollections();
  }

  exportPatientPayments(): void {
    if (this.collectionsPinGateVisible) {
      return;
    }
    if (!this.filters.start_date || !this.filters.end_date) {
      this.toaster.warn('Select start and end date', 'Invalid');
      return;
    }

    const params = {
      start_date: this.formatDate(this.filters.start_date),
      end_date: this.formatDate(this.filters.end_date),
      doctor_id: this.filters.doctor_id ?? '',
    };

    this.exporting = true;
    this.analyticsService.exportPatientPayments(params).subscribe({
      next: (blob: Blob) => {
        this.downloadBlob(blob, `patient_payments_${params.start_date}_to_${params.end_date}.xlsx`);
        this.exporting = false;
      },
      error: (err) => {
        this.exporting = false;
        if (this.relockCollections(err)) {
          return;
        }
        this.toaster.error('Could not export patient payments', 'Failed');
      },
    });
  }

  exportCollections(): void {
    if (this.collectionsPinGateVisible) {
      return;
    }
    if (!this.filters.start_date || !this.filters.end_date) {
      this.toaster.warn('Select start and end date', 'Invalid');
      return;
    }

    const params = {
      start_date: this.formatDate(this.filters.start_date),
      end_date: this.formatDate(this.filters.end_date),
      doctor_id: this.filters.doctor_id ?? '',
    };

    this.exporting = true;
    this.analyticsService.exportCollections(params).subscribe({
      next: (blob: Blob) => {
        this.downloadBlob(blob, `collections_${params.start_date}_to_${params.end_date}.xlsx`);
        this.exporting = false;
      },
      error: (err) => {
        this.exporting = false;
        if (this.relockCollections(err)) {
          return;
        }
        this.toaster.error('Could not export collections', 'Failed');
      }
    });
  }

  exportDailyPatientDetails(date: string): void {
    if (!date || this.collectionsPinGateVisible) {
      return;
    }

    const params = {
      date,
      doctor_id: this.filters.doctor_id ?? ''
    };

    this.exportingDaily = true;
    this.analyticsService.exportDailyPatientDetails(params).subscribe({
      next: (blob: Blob) => {
        this.downloadBlob(blob, `daily_patient_details_${date}.xlsx`);
        this.exportingDaily = false;
      },
      error: (err) => {
        this.exportingDaily = false;
        if (this.relockCollections(err)) {
          return;
        }
        this.toaster.error('Could not export day details', 'Failed');
      }
    });
  }

  get collectionsPinGateVisible(): boolean {
    if (!this.pinRequired) {
      return false;
    }
    if (!this.pinReady || !this.pinUnlocked) {
      return true;
    }
    return this.pinStep !== 'unlock';
  }

  get collectionsPinTitle(): string {
    switch (this.pinStep) {
      case 'create':
        return 'Set a collections PIN';
      case 'confirm':
        return 'Confirm your PIN';
      case 'reset-password':
        return 'Reset collections PIN';
      case 'reset-create':
      case 'change-create':
        return 'Choose a new PIN';
      case 'reset-confirm':
      case 'change-confirm':
        return 'Confirm your new PIN';
      case 'change-current':
        return 'Enter your current PIN';
      default:
        return 'Collections are locked';
    }
  }

  get collectionsPinHint(): string {
    switch (this.pinStep) {
      case 'create':
        return 'Choose 4 digits. This lock is only for financial reports, not patient documents.';
      case 'confirm':
      case 'reset-confirm':
      case 'change-confirm':
        return 'Enter the same 4 digits again.';
      case 'reset-password':
        return 'Enter your login password, then choose a new PIN.';
      case 'reset-create':
      case 'change-create':
        return 'Pick a new 4-digit PIN.';
      case 'change-current':
        return 'Enter the PIN you use for collections.';
      default:
        return 'Enter your 4-digit PIN to see fees, collections, and pending amounts.';
    }
  }

  get collectionsPinFilled(): boolean[] {
    return [0, 1, 2, 3].map((index) => index < this.pinDigits.length);
  }

  @HostListener('document:keydown', ['$event'])
  onCollectionsPinKeydown(event: KeyboardEvent): void {
    if (!this.collectionsPinGateVisible) {
      return;
    }
    const target = event.target as HTMLElement | null;
    const tag = (target?.tagName || '').toUpperCase();
    if (tag === 'INPUT' || tag === 'TEXTAREA') {
      return;
    }
    if (/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      this.pushCollectionsPinDigit(event.key);
      return;
    }
    if (event.key === 'Backspace') {
      event.preventDefault();
      this.popCollectionsPinDigit();
      return;
    }
    if (event.key === 'Escape' && (this.pinStep.startsWith('reset') || this.pinStep.startsWith('change'))) {
      event.preventDefault();
      this.cancelCollectionsPinFlow();
    }
  }

  pushCollectionsPinDigit(digit: string): void {
    if (this.pinBusy || !this.pinReady || this.pinStep === 'reset-password') {
      return;
    }
    if (!/^[0-9]$/.test(digit) || this.pinDigits.length >= 4) {
      return;
    }
    this.pinError = '';
    this.pinDigits += digit;
    if (this.pinDigits.length === 4) {
      this.submitCollectionsPinStep();
    }
  }

  popCollectionsPinDigit(): void {
    if (this.pinBusy || this.pinStep === 'reset-password') {
      return;
    }
    this.pinDigits = this.pinDigits.slice(0, -1);
  }

  startCollectionsPinReset(): void {
    this.clearCollectionsPinEntry();
    this.pinStep = 'reset-password';
  }

  startCollectionsPinChange(): void {
    this.clearCollectionsPinEntry();
    this.pinStep = 'change-current';
  }

  cancelCollectionsPinFlow(): void {
    this.clearCollectionsPinEntry();
    this.pinStep = this.pinConfigured ? 'unlock' : 'create';
  }

  continueCollectionsPinReset(): void {
    if (this.pinPassword.trim().length < 8) {
      this.pinError = 'Enter your login password.';
      return;
    }
    this.pinError = '';
    this.pinDigits = '';
    this.pinStep = 'reset-create';
  }

  lockCollections(): void {
    if (this.pinBusy) {
      return;
    }
    this.pinBusy = true;
    this.analyticsService.lockCollectionsPin().subscribe({
      next: () => {
        this.pinBusy = false;
        this.pinUnlocked = false;
        this.pinStep = this.pinConfigured ? 'unlock' : 'create';
        this.clearCollectionsPinEntry();
        this.hideCollectionsFigures();
      },
      error: (err) => {
        this.pinBusy = false;
        this.toaster.error(extractHttpErrorMessage(err, 'Could not lock collections.'), 'Collections');
      },
    });
  }

  loadCollectionsPinStatus(): void {
    const likelyDoctor = this.requiresCollectionsPin();
    if (likelyDoctor) {
      this.pinRequired = true;
      this.pinUnlocked = false;
      this.pinReady = false;
    }
    this.pinCheckFailed = false;

    this.analyticsService.getCollectionsPinStatus().subscribe({
      next: (res: any) => {
        const data = res?.data || {};
        this.pinRequired = !!data.required;
        this.pinConfigured = !!data.configured;
        this.pinUnlocked = !data.required || !!data.unlocked;
        this.pinReady = true;
        this.pinCheckFailed = false;
        if (data.required && !data.configured) {
          this.pinStep = 'create';
        } else if (this.pinStep !== 'unlock' && this.pinUnlocked) {
          this.pinStep = 'unlock';
        }
        if (Number(data.retry_after) > 0) {
          this.pinError = `Too many attempts. Try again in ${data.retry_after} seconds.`;
        }
        if (!this.collectionsPinGateVisible) {
          this.loadReport();
        }
      },
      error: () => {
        if (!likelyDoctor) {
          this.pinRequired = false;
          this.pinUnlocked = true;
          this.pinReady = true;
          this.loadReport();
          return;
        }
        this.pinRequired = true;
        this.pinUnlocked = false;
        this.pinReady = true;
        this.pinCheckFailed = true;
        this.pinError = 'Could not check the collections lock.';
      },
    });
  }

  private submitCollectionsPinStep(): void {
    const pin = this.pinDigits;
    if (pin.length !== 4) {
      this.pinError = 'Enter all 4 digits.';
      return;
    }
    if (/^(\d)\1{3}$/.test(pin) && this.pinStep !== 'unlock' && this.pinStep !== 'change-current') {
      this.pinError = 'Choose a PIN that is not four identical digits.';
      this.pinDigits = '';
      return;
    }

    if (this.pinStep === 'create' || this.pinStep === 'reset-create' || this.pinStep === 'change-create') {
      this.pinDraft = pin;
      this.pinDigits = '';
      this.pinStep = this.pinStep === 'create'
        ? 'confirm'
        : this.pinStep === 'reset-create'
          ? 'reset-confirm'
          : 'change-confirm';
      return;
    }

    if (this.pinStep === 'confirm' || this.pinStep === 'reset-confirm' || this.pinStep === 'change-confirm') {
      if (pin !== this.pinDraft) {
        this.pinError = 'Those PINs do not match. Enter it again.';
        this.pinDigits = '';
        this.pinDraft = '';
        this.pinStep = this.pinStep === 'confirm'
          ? 'create'
          : this.pinStep === 'reset-confirm'
            ? 'reset-create'
            : 'change-create';
        return;
      }
      this.saveCollectionsPin(pin);
      return;
    }

    if (this.pinStep === 'change-current') {
      this.pinCurrent = pin;
      this.pinDigits = '';
      this.pinStep = 'change-create';
      return;
    }

    this.verifyCollectionsPin(pin);
  }

  private saveCollectionsPin(pin: string): void {
    const body: { pin: string; pin_confirmation: string; current_pin?: string; password?: string } = {
      pin,
      pin_confirmation: pin,
    };
    if (this.pinStep === 'change-confirm') {
      body.current_pin = this.pinCurrent;
    }
    if (this.pinStep === 'reset-confirm') {
      body.password = this.pinPassword;
    }

    this.pinBusy = true;
    this.pinError = '';
    this.analyticsService.setCollectionsPin(body).subscribe({
      next: () => {
        this.pinBusy = false;
        this.pinConfigured = true;
        this.pinUnlocked = true;
        this.pinStep = 'unlock';
        this.clearCollectionsPinEntry();
        this.toaster.success('Collections PIN saved.', 'Collections');
        this.loadReport();
      },
      error: (err) => {
        this.pinBusy = false;
        this.pinDigits = '';
        this.pinError = extractHttpErrorMessage(err, 'Could not save the PIN.');
        if (this.pinStep === 'change-confirm') {
          this.pinStep = 'change-current';
          this.pinCurrent = '';
          this.pinDraft = '';
        }
      },
    });
  }

  private verifyCollectionsPin(pin: string): void {
    this.pinBusy = true;
    this.pinError = '';
    this.analyticsService.verifyCollectionsPin(pin).subscribe({
      next: () => {
        this.pinBusy = false;
        this.pinUnlocked = true;
        this.pinConfigured = true;
        this.pinStep = 'unlock';
        this.clearCollectionsPinEntry();
        this.loadReport();
      },
      error: (err) => {
        this.pinBusy = false;
        this.pinDigits = '';
        this.pinError = extractHttpErrorMessage(err, 'Incorrect PIN.');
      },
    });
  }

  private clearCollectionsPinEntry(): void {
    this.pinDigits = '';
    this.pinDraft = '';
    this.pinCurrent = '';
    this.pinPassword = '';
    this.pinError = '';
  }

  private hideCollectionsFigures(): void {
    this.hasGenerated = false;
    this.visibleModal = false;
    this.dailyDetails = [];
    this.report.daily_breakdown = [];
    this.report.summary = {
      total_appointments: 0,
      fees_generated: 0,
      cash_collected: 0,
      online_collected: 0,
      total_collected: 0,
      total_pending: 0,
    };
  }

  private relockCollections(err: unknown): boolean {
    const status = (err as { status?: number })?.status;
    const message = extractHttpErrorMessage(err, '');
    if (status !== 423 && !/collections pin/i.test(message)) {
      return false;
    }
    this.pinRequired = true;
    this.pinUnlocked = false;
    this.pinStep = 'unlock';
    this.hideCollectionsFigures();
    return true;
  }

  private requiresCollectionsPin(): boolean {
    try {
      const user = JSON.parse(sessionStorage.getItem('loggedInUser') || '{}');
      const roles = Array.isArray(user?.roles)
        ? user.roles.map((role: unknown) => String(role).toLowerCase())
        : [];
      if (roles.includes('assistant_doctor')) {
        return false;
      }
      const type = String(user?.type || user?.user_type || '').toLowerCase();
      return type === 'doctor' || roles.includes('doctor');
    } catch {
      return false;
    }
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }
}
